window.JRS = window.JRS || {};

(function(JRS) {
  'use strict';

  var EventBus = JRS.EventBus;

  var MAX_RETRIES = 3;
  var BASE_DELAY_MS = 500;
  var VERIFY_DELAY_MS = 1200;

  // --- Mutex ---
  var _queue = [];
  var _processing = false;

  /**
   * @param {function(): Promise<any>} fn
   * @returns {Promise<any>}
   */
  function _enqueue(fn) {
    return new Promise(function(resolve, reject) {
      _queue.push({ fn: fn, resolve: resolve, reject: reject });
      if (!_processing) {
        _processQueue();
      }
    });
  }

  function _processQueue() {
    if (_queue.length === 0) {
      _processing = false;
      return;
    }

    _processing = true;
    var item = _queue.shift();

    item.fn()
      .then(item.resolve)
      .catch(item.reject)
      .finally(function() {
        _processQueue();
      });
  }

  /**
   * @param {string} url
   * @param {{ method?: string, body?: Object }} [options]
   * @returns {Promise<any>}
   */
  // ERP parity (2026-08-04) — deep bracket serialization, equivalent to
  // jQuery.param(obj) with traditional=false. The CONTROL theme submits its
  // bundle add form-encoded (jQuery.ajax with no contentType), which is what
  // makes Shopify store the nested `_bundle_raw` structure as a nested hash
  // ("0" => {...}) with all-string scalars — the shape the ERP parses. When a
  // payload carries structured (non-scalar) property values we must submit the
  // same way; JSON.stringify would flatten it to a quoted string.
  function _formSerialize(obj, prefix, pairs) {
    pairs = pairs || [];
    if (obj === null || typeof obj === 'undefined') {
      pairs.push(encodeURIComponent(prefix) + '=');
      return pairs;
    }
    if (Array.isArray(obj)) {
      for (var i = 0; i < obj.length; i++) {
        _formSerialize(obj[i], prefix + '[' + i + ']', pairs);
      }
      return pairs;
    }
    if (typeof obj === 'object') {
      for (var k in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, k)) {
          _formSerialize(obj[k], prefix ? prefix + '[' + k + ']' : k, pairs);
        }
      }
      return pairs;
    }
    pairs.push(encodeURIComponent(prefix) + '=' + encodeURIComponent(String(obj)));
    return pairs;
  }

  /** True when any item's properties contain a non-scalar value (e.g. the
      control-shape `_bundle_raw` structure). */
  function _hasStructuredProperties(body) {
    var items = (body && body.items) || [];
    for (var i = 0; i < items.length; i++) {
      var props = items[i] && items[i].properties;
      if (!props) continue;
      // Reference v1.1 §2.2 correction (2026-08-20): form encoding IS the
      // production transport — control's string-scalar, object-keyed
      // `_bundle_raw` is what Tracker ingests. The earlier JSON-forcing
      // exemption is reverted.
      for (var k in props) {
        if (Object.prototype.hasOwnProperty.call(props, k) &&
            props[k] !== null && typeof props[k] === 'object') {
          return true;
        }
      }
    }
    return false;
  }

  function _fetchJSON(url, options) {
    var method = (options && options.method) || 'GET';
    var fetchOptions = {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    };

    if (options && typeof options.body !== 'undefined') {
      // Structured property values (nested _bundle_raw) must go form-encoded
      // so Shopify parses the brackets into a nested hash, exactly like the
      // control theme's jQuery form POST. Everything else stays JSON.
      if (options.form === true ||
          (url.indexOf('/cart/add') === 0 && _hasStructuredProperties(options.body))) {
        fetchOptions.headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8';
        fetchOptions.body = _formSerialize(options.body, '', []).join('&');
      } else {
        fetchOptions.body = JSON.stringify(options.body);
      }
    }

    return fetch(url, fetchOptions).then(function(response) {
      var contentType = response.headers.get('content-type') || '';
      var isJSON = contentType.indexOf('application/json') !== -1;

      return response.text().then(function(text) {
        var trimmed = String(text || '').trim();
        var looksLikeJSON = trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[';
        var parsed = null;

        if (trimmed.length > 0 && (isJSON || looksLikeJSON)) {
          try {
            parsed = JSON.parse(trimmed);
          } catch (parseError) {
            parsed = null;
          }
        }

        if (!response.ok) {
          var err = new Error('HTTP ' + response.status);
          err.status = response.status;
          // v8.1.1 — Shopify's 429 is its bot-protection interstitial
          // ("Verifying your connection..."), an HTML page, not a JSON throttle.
          // It has no Retry-After, blocks GET and POST alike for minutes, and can
          // only be cleared by a page navigation. Flag it so callers never retry
          // into it (each retry is another request against the same block).
          err.challenge = response.status === 429 || /Verifying your connection/i.test(trimmed);
          if (err.challenge) err.status = 429;
          if (parsed && typeof parsed === 'object') {
            parsed._httpStatus = response.status;
            err.data = parsed;
          } else {
            err.data = { _httpStatus: response.status, _raw: true, _text: trimmed };
          }
          throw err;
        }

        if (parsed && typeof parsed === 'object') {
          parsed._httpStatus = response.status;
          // v8.2.0 — Shopify serves /cart.js from its own cart cache and stamps the
          // response with a watermark (x-cartjs-updatedat, unix seconds) + x-cartjs-cache.
          // Exposed so verification can PROVE a read is stale instead of guessing.
          try {
            var wm = response.headers.get('x-cartjs-updatedat');
            if (wm !== null && wm !== '') parsed._cartjsUpdatedAt = Number(wm);
            var cc = response.headers.get('x-cartjs-cache');
            if (cc !== null) parsed._cartjsCache = String(cc);
          } catch (eHdr) {}
          return parsed;
        }

        return { _httpStatus: response.status, _raw: true, _text: trimmed };
      });
    });
  }

  /**
   * @param {string} url
   * @param {{ method?: string, body?: Object }} options
   * @param {number} retriesLeft
   * @returns {Promise<any>}
   */
  function _retryOn409(url, options, retriesLeft) {
    return _fetchJSON(url, options).catch(function(err) {
      if (err && err.status === 409 && retriesLeft > 0) {
        var delay = BASE_DELAY_MS * Math.pow(2, MAX_RETRIES - retriesLeft);
        return _wait(delay).then(function() {
          return _retryOn409(url, options, retriesLeft - 1);
        });
      }
      throw err;
    });
  }

  /**
   * @param {number} delayMs
   * @returns {Promise<void>}
   */
  function _wait(delayMs) {
    return new Promise(function(resolve) {
      setTimeout(resolve, delayMs);
    });
  }

  /**
   * @param {any} err
   * @returns {string}
   */
  function _extractErrorMessage(err) {
    if (!err) return 'Unknown cart error';
    if (err.data && typeof err.data.description === 'string' && err.data.description.length > 0) {
      return err.data.description;
    }
    if (err.data && typeof err.data.message === 'string' && err.data.message.length > 0) {
      return err.data.message;
    }
    if (typeof err.message === 'string' && err.message.length > 0) {
      return err.message;
    }
    return 'Unknown cart error';
  }

  /**
   * @returns {string}
   */
  function _generateUUID() {
    if (JRS && typeof JRS.generateUUID === 'function') {
      return JRS.generateUUID();
    }
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = Math.random() * 16 | 0;
      var v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * @param {import('./jrs-v2-types.js').CartPayload} payload
   * @returns {string}
   */
  function _resolveInstanceId(payload) {
    var firstItem = payload && payload.items && payload.items[0];
    var firstProperties = firstItem && firstItem.properties ? firstItem.properties : null;
    var fromPayload = firstProperties && firstProperties._bundle_instance_id;
    if (typeof fromPayload === 'string' && fromPayload.length > 0) {
      return fromPayload;
    }
    return _generateUUID();
  }

  /**
   * POST /cart/add.js with JSON body.
   * All cart adds go through this single function.
   *
   * @param {import('./jrs-v2-types.js').CartPayload} payload
   * @returns {Promise<{ ok: boolean, response: Object|null, error: string|null }>}
   */
  function addItems(payload, options) {
    return _enqueue(function() {
      var instanceId = _resolveInstanceId(payload || { items: [] });
      var body = { items: (payload && payload.items) || [] };
      // v8.1.1 — a multi-POST submit passes skipCartRefresh:true for every group
      // but the last, so a bundle no longer spends one GET /cart.js per POST
      // (that doubled the request count that trips Shopify's rate protection).
      var skipRefresh = !!(options && options.skipCartRefresh);

      return _retryOn409('/cart/add.js', { method: 'POST', body: body }, MAX_RETRIES)
        .then(function(addResponse) {
          EventBus.emit('cart:addSucceeded', {
            items: body.items,
            instanceId: instanceId,
            // 2026-09-15 — additive: the parsed /cart/add.js response ({ items: [line] }
            // for an items[] body). jrs-v2-analytics.js reads identifiers and price from
            // it so jrs_add_to_cart describes the lines Shopify actually added, not page
            // context. Existing subscribers (toast, selection store) ignore it.
            response: addResponse
          });

          if (skipRefresh) {
            return { ok: true, response: addResponse, error: null };
          }
          return getCart().then(function(cart) {
            EventBus.emit('cart:updated', { cart: cart });
            return { ok: true, response: addResponse, error: null };
          });
        })
        .catch(function(err) {
          var message = _extractErrorMessage(err);

          // 422 is a Shopify validation error and should not be retried.
          if (err && err.status === 422) {
            message = _extractErrorMessage(err);
          }

          // Instrumentation (2026-09-03): carry the HTTP status and Shopify's raw
          // `description` alongside the message so the caller's telemetry can
          // classify the failure. Additive — behaviour of the add is unchanged.
          var httpStatus = (err && typeof err.status === 'number') ? err.status : 0;
          var shopifyDescription = (err && err.data && typeof err.data.description === 'string') ? err.data.description : '';

          EventBus.emit('cart:addFailed', {
            error: message,
            httpStatus: httpStatus,
            shopifyDescription: shopifyDescription,
            payload: payload || { items: [] },
            instanceId: instanceId
          });

          return { ok: false, response: null, error: message, httpStatus: httpStatus, shopifyDescription: shopifyDescription, challenge: !!(err && err.challenge) };
        });
    });
  }

  /**
   * GET /cart.js — returns current cart state.
   * Does not use mutex.
   *
   * @returns {Promise<import('./jrs-v2-types.js').ShopifyCartResponse>}
   */
  function getCart() {
    return _fetchJSON('/cart.js', { method: 'GET' });
  }

  /**
   * GET /cart?sections=<ids> — Shopify Section Rendering API. Returns the freshly
   * server-rendered HTML for the given section id(s) so the cart page can re-render
   * discount messaging / prices that Liquid computes (and JS can't) after a change.
   * @param {string|string[]} ids  one section id or a list
   * @returns {Promise<Object<string,string>>} { "<section-id>": "<html>" }
   */
  function getSections(ids) {
    var param = Array.isArray(ids) ? ids.join(',') : String(ids || '');
    // Deliberately NOT via _fetchJSON: its `Accept: application/json` header makes
    // Shopify return the /cart.js cart object instead of the Section Rendering payload
    // ({ "<section-id>": "<html>" }). A plain fetch (browser-default Accept) returns the
    // sections — matching a raw fetch('/cart?sections=…').then(r => r.json()).
    return fetch('/cart?sections=' + encodeURIComponent(param), { method: 'GET' })
      .then(function (r) {
        if (!r.ok) throw new Error('getSections HTTP ' + r.status);
        return r.json();
      });
  }

  /**
   * v8.2.0 — authoritative cart read that bypasses Shopify's cart cache: an empty
   * /cart/update.js POST returns the cart body from the WRITE path (no
   * x-cartjs-cache header). Used by verification only after a cached read has
   * missed lines — never on the normal add path (it costs a request).
   * @returns {Promise<Object>} the cart JSON
   */
  function readCartAuthoritative() {
    return _enqueue(function() {
      return _retryOn409('/cart/update.js', { method: 'POST', body: { updates: {} } }, MAX_RETRIES);
    });
  }

  /**
   * POST /cart/update.js — batch quantity updates.
   *
   * @param {Object<string, number>} updates
   * @returns {Promise<{ ok: boolean, cart: Object|null, error: string|null }>}
   */
  function updateItems(updates) {
    return _enqueue(function() {
      return _retryOn409('/cart/update.js', {
        method: 'POST',
        body: { updates: updates || {} }
      }, MAX_RETRIES)
        .then(function() {
          return getCart().then(function(cart) {
            EventBus.emit('cart:updated', { cart: cart });
            return { ok: true, cart: cart, error: null };
          });
        })
        .catch(function(err) {
          // httpStatus / challenge are additive (2026-09-14 cart-edit add-before-remove):
          // the edit flow must tell a 409 (retry once) from a 429 challenge (stop).
          return { ok: false, cart: null, error: _extractErrorMessage(err),
            httpStatus: (err && typeof err.status === 'number') ? err.status : 0,
            challenge: !!(err && err.challenge) };
        });
    });
  }

  /**
   * POST /cart/update.js — update cart-level attributes and/or note.
   *
   * @param {Object<string, string>} [attributes]
   * @param {string} [note]
   * @returns {Promise<{ ok: boolean, error: string|null }>}
   */
  function updateAttributes(attributes, note) {
    return _enqueue(function() {
      var body = {};
      if (attributes && typeof attributes === 'object') {
        body.attributes = attributes;
      }
      if (typeof note === 'string') {
        body.note = note;
      }

      return _retryOn409('/cart/update.js', { method: 'POST', body: body }, MAX_RETRIES)
        .then(function() {
          return { ok: true, error: null };
        })
        .catch(function(err) {
          return { ok: false, error: _extractErrorMessage(err) };
        });
    });
  }

  /**
   * Remove multiple items by cart line key.
   *
   * @param {string[]} keys
   * @returns {Promise<{ ok: boolean, cart: Object|null, error: string|null }>}
   */
  function removeItems(keys) {
    var updates = {};
    var list = keys || [];
    for (var i = 0; i < list.length; i++) {
      updates[list[i]] = 0;
    }
    return updateItems(updates);
  }

  /**
   * D-1 (Bug F identity): remove child line items SEQUENTIALLY by their stable
   * `_lineItemKey`, re-resolving the current Shopify line key from the freshest
   * cart before each removal. removeItems()'s batch `/cart/update.js` `updates`
   * hash collapses sibling children that share one service variant id (Shopify
   * reads the numeric key prefix as a variant id), silently driving the loser to
   * 0 — the canonical Bug F deletion hazard. This primitive removes exactly the
   * intended lines even when many children share a variant. ADDITIVE: the remove
   * cascades migrate to it in D-1 step 2; existing removeItems() callers are
   * unchanged. If the cart is unreadable (e.g. a Bug W non-JSON sentinel), it
   * aborts with ok:false rather than silently reporting everything not-found.
   *
   * @param {string[]} lineItemKeys  stable `_lineItemKey` values to remove
   * @returns {Promise<{ ok: boolean, removed: number, notFound: number, failed: number, cart: Object|null, error: string|null }>}
   */
  function removeItemsByLineItemKey(lineItemKeys) {
    var targets = [];
    var seen = {};
    var src = lineItemKeys || [];
    for (var i = 0; i < src.length; i++) {
      var lik = String(src[i] || '');
      if (lik && !seen[lik]) { seen[lik] = true; targets.push(lik); }
    }
    var removed = 0, notFound = 0, failed = 0;
    function findKey(cart, lik) {
      var items = (cart && cart.items) || [];
      for (var j = 0; j < items.length; j++) {
        var p = items[j].properties || {};
        if (String(p._lineItemKey || '') === lik) { return String(items[j].key); }
      }
      return null;
    }
    function step(idx, cart) {
      if (idx >= targets.length) {
        return Promise.resolve({ ok: failed === 0, removed: removed, notFound: notFound, failed: failed, cart: cart || null, error: null });
      }
      var resolveCart = cart ? Promise.resolve(cart) : getCart();
      return resolveCart.then(function(c) {
        if (!c || !Array.isArray(c.items)) {
          // Cart unreadable (Bug W sentinel / non-JSON) — abort instead of reporting all-not-found.
          failed += (targets.length - idx);
          return { ok: false, removed: removed, notFound: notFound, failed: failed, cart: c || null, error: 'cart_unreadable' };
        }
        var shopKey = findKey(c, targets[idx]);
        if (!shopKey) { notFound++; return step(idx + 1, c); }
        return changeItem(shopKey, { quantity: 0 }).then(function(res) {
          if (res && res.ok) { removed++; return step(idx + 1, res.cart); }
          failed++; return step(idx + 1, (res && res.cart) ? res.cart : c);
        });
      });
    }
    return step(0, null);
  }

  /**
   * v8.1.1 (orphaned bundle instances — orders #72575, #73582) — remove EVERY cart
   * line that belongs to one bundle / customisation instance, by the line's own
   * Shopify key, in one /cart/update.js batch, then re-read and sweep stragglers.
   *
   * Why not removeItemsByLineItemKey: it dedupes `_lineItemKey` targets and then
   * resolves each key to the FIRST cart line carrying it. Under the per-size split
   * sibling children share a `_lineItemKey` (Doc 2 §6.3), and two instances of the
   * same bundle share every key — so removing one bundle parent removed one line
   * per key, possibly from the OTHER instance, and left the rest as paid orphans.
   *
   * @param {{ bundleInstanceId?: string, customizationInstanceId?: string, parentVariantId?: (string|number), includeParent?: boolean, cart?: Object }} scope
   * @returns {Promise<{ ok: boolean, removed: number, remaining: number, cart: Object|null, error: string|null }>}
   */
  function removeItemsByInstance(scope) {
    var bundleId = String((scope && scope.bundleInstanceId) || '');
    var customId = String((scope && scope.customizationInstanceId) || '');
    var includeParent = !(scope && scope.includeParent === false);
    // HotFix 2026-09-25 (remove one size row emptied the cart): one standard add
    // writes ONE _customization_instance_id onto a parent line PER SIZE plus every
    // size's children, and the cart page renders each size as its own row. Scoping
    // by the instance alone therefore removed every size of that add. When set,
    // parentVariantId narrows the sweep to that one size: its parent line
    // (variant_id) and its children (_ParentVariant). Omitted = previous behaviour.
    var parentVariantId = String((scope && scope.parentVariantId) || '');
    if (!bundleId && !customId) {
      return Promise.resolve({ ok: false, removed: 0, remaining: 0, cart: null, error: 'instance id required' });
    }
    function belongs(item) {
      var p = (item && item.properties) || {};
      var isChild = typeof p._ParentVariant !== 'undefined';
      if (!includeParent && !isChild) return false;
      if (parentVariantId) {
        var lineVariant = isChild ? String(p._ParentVariant) : String((item && item.variant_id) || '');
        if (lineVariant !== parentVariantId) return false;
      }
      if (bundleId && String(p._bundle_instance_id || '') === bundleId) return true;
      if (customId && String(p._customization_instance_id || '') === customId) return true;
      return false;
    }
    function keysFor(cart) {
      var out = [];
      var items = (cart && cart.items) || [];
      for (var i = 0; i < items.length; i++) { if (belongs(items[i])) out.push(String(items[i].key)); }
      return out;
    }
    var removedTotal = 0;
    function pass(cart, attempt) {
      var keys = keysFor(cart);
      if (keys.length === 0) {
        return Promise.resolve({ ok: true, removed: removedTotal, remaining: 0, cart: cart || null, error: null });
      }
      if (attempt >= 3) {
        return Promise.resolve({ ok: false, removed: removedTotal, remaining: keys.length, cart: cart || null, error: 'instance_lines_remain' });
      }
      var updates = {};
      for (var k = 0; k < keys.length; k++) updates[keys[k]] = 0;
      return updateItems(updates).then(function(res) {
        if (!res || !res.ok) {
          return { ok: false, removed: removedTotal, remaining: keys.length, cart: (res && res.cart) || cart || null, error: (res && res.error) || 'update_failed',
            httpStatus: (res && res.httpStatus) || 0, challenge: !!(res && res.challenge) };
        }
        var after = keysFor(res.cart);
        removedTotal += Math.max(0, keys.length - after.length);
        // Shopify can re-key lines within a batch; re-read and sweep until clean.
        return pass(res.cart, attempt + 1);
      });
    }
    var start = (scope && scope.cart && Array.isArray(scope.cart.items)) ? Promise.resolve(scope.cart) : getCart();
    return start.then(function(cart) { return pass(cart, 0); }).catch(function(err) {
      return { ok: false, removed: removedTotal, remaining: -1, cart: null, error: _extractErrorMessage(err),
        httpStatus: (err && typeof err.status === 'number') ? err.status : 0, challenge: !!(err && err.challenge) };
    });
  }

  /**
   * 2026-09-26 (ClickUp 86eywgb42) — remove every line for which `match(item)` is
   * true, ONE /cart/change.js per line, by that line's own Shopify key. The next
   * target is picked from the cart that change.js returns (the write path, not the
   * cached /cart.js), so a removed line is never targeted twice.
   *
   * Why not removeItems / removeItemsByInstance: both send one /cart/update.js
   * `updates` hash. Reproduced on the BYOB preview theme 2026-09-26 (3 of 3 runs):
   * with an edit's original AND replacement lines in the cart, a batch zero of the
   * 4 original keys left both original position lines and removed a replacement
   * position line of the same variant. change.js by key removed exactly the 4
   * originals in 3 of 3 runs. Mechanism (store read, same day): adding a line
   * re-keys the earlier lines of the same variant, so keys snapshotted before the
   * add go stale; update.js then reads a stale key as its variant prefix and zeroes
   * the first line of that variant. Picking each key from the latest cart avoids it.
   *
   * Stops at the first failed request. Emits cart:updated once, at the end.
   *
   * @param {function(Object): boolean} match
   * @param {{ cart?: Object }} [opts]  a cart to pick the first target from
   * @returns {Promise<{ ok: boolean, removed: number, cart: Object|null, error: string|null, httpStatus: number, challenge: boolean }>}
   */
  function removeLinesSequential(match, opts) {
    var removed = 0;
    var cap = 0;
    function first(cart) {
      var items = (cart && cart.items) || [];
      for (var i = 0; i < items.length; i++) { if (match(items[i])) return String(items[i].key); }
      return null;
    }
    function finish(cart, extra) {
      if (removed > 0 && cart) EventBus.emit('cart:updated', { cart: cart });
      var out = { ok: true, removed: removed, cart: cart || null, error: null, httpStatus: 0, challenge: false };
      if (extra) { for (var k in extra) { if (Object.prototype.hasOwnProperty.call(extra, k)) out[k] = extra[k]; } }
      return out;
    }
    function step(cart) {
      if (!cart || !Array.isArray(cart.items)) {
        return finish(cart, { ok: false, error: 'cart_unreadable' });
      }
      var key = first(cart);
      if (!key) return finish(cart);
      if (cap === 0) cap = cart.items.length + 3;
      if (removed >= cap) return finish(cart, { ok: false, error: 'lines_remain' });
      return _enqueue(function() {
        return _retryOn409('/cart/change.js', { method: 'POST', body: { id: key, quantity: 0 } }, MAX_RETRIES);
      }).then(function(nextCart) {
        removed++;
        return step(nextCart);
      }, function(err) {
        return finish(cart, { ok: false, error: _extractErrorMessage(err),
          httpStatus: (err && typeof err.status === 'number') ? err.status : 0, challenge: !!(err && err.challenge) });
      });
    }
    var start = (opts && opts.cart && Array.isArray(opts.cart.items)) ? Promise.resolve(opts.cart) : readCartAuthoritative();
    return start.then(step, function(err) {
      return finish(null, { ok: false, error: _extractErrorMessage(err),
        httpStatus: (err && typeof err.status === 'number') ? err.status : 0, challenge: !!(err && err.challenge) });
    });
  }

  /**
   * POST /cart/change.js — modify single item quantity/properties.
   *
   * @param {string|number} keyOrLine
   * @param {Object} changes
   * @returns {Promise<{ ok: boolean, cart: Object|null, error: string|null }>}
   */
  function changeItem(keyOrLine, changes) {
    return _enqueue(function() {
      var body = {};
      if (typeof keyOrLine === 'string') {
        body.id = keyOrLine;
      } else if (typeof keyOrLine === 'number') {
        body.line = keyOrLine;
      } else {
        return Promise.resolve({
          ok: false,
          cart: null,
          error: 'changeItem requires a cart line key (string) or line number (number)'
        });
      }

      var patch = changes && typeof changes === 'object' ? changes : {};
      var patchKeys = Object.keys(patch);
      for (var i = 0; i < patchKeys.length; i++) {
        var key = patchKeys[i];
        body[key] = patch[key];
      }

      return _retryOn409('/cart/change.js', { method: 'POST', body: body }, MAX_RETRIES)
        .then(function() {
          return getCart().then(function(cart) {
            EventBus.emit('cart:updated', { cart: cart });
            return { ok: true, cart: cart, error: null };
          });
        })
        .catch(function(err) {
          return { ok: false, cart: null, error: _extractErrorMessage(err) };
        });
    });
  }

  /**
   * Post-add verification to detect Shopify async merge (B2).
   *
   * @param {import('./jrs-v2-types.js').CartPayload} expectedPayload
   * @returns {Promise<{ verified: boolean, expectedCount: number, actualCount: number, missingItems: Object[] }>}
   */
  function verifyAfterAdd(expectedPayload) {
    var expectedItems = (expectedPayload && expectedPayload.items) || [];

    return _wait(VERIFY_DELAY_MS).then(function() {
      return getCart().then(function(cart) {
        var actualItems = cart && cart.items ? cart.items : [];
        var missingItems = [];

        for (var i = 0; i < expectedItems.length; i++) {
          var expectedItem = expectedItems[i];
          var expectedProps = expectedItem && expectedItem.properties ? expectedItem.properties : null;
          var expectedLineKey = expectedProps && expectedProps._lineItemKey;

          if (!expectedLineKey) {
            continue;
          }

          var found = false;
          for (var j = 0; j < actualItems.length; j++) {
            var actualProps = actualItems[j] && actualItems[j].properties ? actualItems[j].properties : null;
            if (actualProps && actualProps._lineItemKey === expectedLineKey) {
              found = true;
              break;
            }
          }

          if (!found) {
            missingItems.push(expectedItem);
          }
        }

        return {
          verified: missingItems.length === 0,
          expectedCount: expectedItems.length,
          actualCount: actualItems.length,
          missingItems: missingItems
        };
      });
    });
  }

  JRS.CartAPI = {
    addItems: addItems,
    getCart: getCart,
    getSections: getSections,
    updateItems: updateItems,
    updateAttributes: updateAttributes,
    removeItems: removeItems,
    removeItemsByLineItemKey: removeItemsByLineItemKey,
    removeItemsByInstance: removeItemsByInstance, // v8.1.1 — instance-exact removal
    removeLinesSequential: removeLinesSequential, // 2026-09-26 — one change.js per line (cart-edit)
    readCartAuthoritative: readCartAuthoritative, // v8.2.0 — write-path cart read (bypasses the cart cache)
    changeItem: changeItem,
    verifyAfterAdd: verifyAfterAdd
  };
})(window.JRS);
